import { customGatewayAdapter } from "../adapters/customGatewayAdapter";
import { iframeAdapter } from "../adapters/iframeAdapter";
import { popupAdapter } from "../adapters/popupAdapter";
import { redirectAdapter } from "../adapters/redirectAdapter";
import type { StartPayConfig } from "../types/config";
import type { PaymentStatusInput, StartPaymentRequest, VerifyPaymentInput } from "../types/payment";
import type { StartPayEventHandler, StartPayEventName } from "../types/events";
import type { StartPayResponse } from "../types/response";
import { createLogger } from "../utils/logger";
import { createPaymentSession } from "./createPaymentSession";
import { createEventEmitter } from "./events";
import { normalizeError } from "./normalizeResponse";
import { cancelPayment as cancelPaymentRequest, getPaymentStatus as getPaymentStatusRequest } from "./paymentStatus";
import { validateConfig, validatePaymentInput } from "./validators";
import { verifyPayment as verifyPaymentRequest } from "./verifyPayment";

const adapters = {
  custom: customGatewayAdapter,
  iframe: iframeAdapter,
  popup: popupAdapter,
  redirect: redirectAdapter
} as const;

export type StartPayClient = ReturnType<typeof createStartPayClient>;

export const createStartPayClient = (initialConfig: StartPayConfig) => {
  let config = { mode: "popup" as const, ...initialConfig };
  validateConfig(config);

  const emitter = createEventEmitter();
  let logger = createLogger(config.debug);

  const emitResult = (response: StartPayResponse) => {
    if (response.status === "success") emitter.emit("payment:success", response);
    if (response.status === "failed") emitter.emit("payment:failed", response);
    if (response.status === "pending") emitter.emit("payment:pending", response);
    if (response.status === "cancelled") emitter.emit("payment:cancelled", response);
    if (response.status === "error") emitter.emit("payment:error", response);
    return response;
  };

  return {
    async startPayment(input: StartPaymentRequest) {
      try {
        validatePaymentInput(input);
        emitter.emit("payment:start", input);
        logger.debug("Creating payment session", input.orderId);

        const session = await createPaymentSession(config, input, input.signal);
        emitter.emit("payment:session", session);

        const adapter = adapters[config.mode ?? "popup"];
        const response = await adapter(session, input);
        return emitResult(response);
      } catch (error) {
        return emitResult(normalizeError(error, input.orderId));
      }
    },
    verifyPayment(input: VerifyPaymentInput) {
      return verifyPaymentRequest(config, input);
    },
    getPaymentStatus(input: PaymentStatusInput) {
      return getPaymentStatusRequest(config, input);
    },
    cancelPayment(input: PaymentStatusInput) {
      return cancelPaymentRequest(config, input);
    },
    on<T extends StartPayEventName>(event: T, handler: StartPayEventHandler<T>) {
      return emitter.on(event, handler);
    },
    off<T extends StartPayEventName>(event: T, handler: StartPayEventHandler<T>) {
      emitter.off(event, handler);
    },
    setConfig(nextConfig: Partial<StartPayConfig>) {
      config = { ...config, ...nextConfig };
      validateConfig(config);
      logger = createLogger(config.debug);
    }
  };
};
